import {
  AdminHeader,
  AdminOptionsDropdown,
  AdminSpinner,
  AllProvidersModal,
  AnimatedDiv,
  Brand,
  BrandImageComponent,
  BrandPausedTimeEntity,
  ClockSolidIcon,
  ClosingDaysPageIcon,
  CopyTextWithIcon,
  DeliveriesIcon,
  Div,
  DropdownDeleteIcon,
  DropdownLocationIcon,
  DropdownMenuIcon,
  DropdownNotesIcon,
  DropdownProvidersIcon,
  DropdownVDriveIcon,
  ErrorToast,
  H3,
  H5,
  LocationMode,
  MenuNamesResponse,
  OrderIcon,
  Provider,
  ProviderStatus,
  RenderProviderStatus,
  SalesStaticResponse,
  VDriveStatus,
  WorkingHoursIcon,
  getBrandById,
  getBrandProviderStatuses,
  getBrandSalesData,
  getBrandsPausedTimes,
  getLocationStoreName,
  lightTheme,
  setTimeTo,
  useAuthenticatedAuthUser,
  useVDrive,
  reportError,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AllProvidersModalComponent } from '../../../../../components/src/shared/dashboard/AllProvidersModalComponent';
import { BrandAddModal } from './BrandAdd';
import { BrandNoteModal } from './BrandNoteModal';
import { DeleteLocationBrandModal } from './DeleteLocationBrandModal';

import { BrandAnalyticsSection } from './BrandAnalyticsSection';
import {
  AddBrandPausedTimesModal,
  BrandPausedTimePill,
  BrandPausedTimesModal,
} from './BrandPausedTImes';
import { PushOrdersToPosPill } from './PushOrdersToPosPill';
import { MenuHealthReportsModal } from './menuHealth/MenuHealthReportsModal';
import { MenulyticsMark } from './menuHealth/components/MenulyticsMark';

export const BrandDetails = () => {
  const navigate = useNavigate();
  const { setDeliveryAssignment, setBrandVDrive } = useVDrive();

  const [addBrandModal, setAddBrandModal] = useState(false);
  const [noteModal, setNoteModal] = useState(false);
  const [menuHealthModal, setMenuHealthModal] = useState(false);

  const today = new Date();

  const [loading, setLoading] = useState(false);
  const { id } = useParams() as { id: string };

  const [data, setData] = useState<SalesStaticResponse | null>(null);
  const [details, setDetails] = useState<Brand | null>(null);
  const [statuses, setStatuses] = useState<
    { provider: Provider; status: ProviderStatus }[]
  >([]);

  const [date, setDate] = useState([today, today]);
  const [dateInput, setDateInput] = useState([today, today]);

  const [allProvidersModal, setAllProvidersModal] =
    useState<AllProvidersModal | null>(null);

  const [deleteModal, setDeleteModal] = useState<string | null>(null);

  const [pausedTimes, setPausedTimes] = useState<BrandPausedTimeEntity[]>([]);
  const [pausedTimeModal, setPausedTimeModal] = useState(false);
  const [addPausedTimeModal, setAddPausedTimeModal] = useState(false);
  const [editPausedTimeModal, setEditPausedTimeModal] =
    useState<BrandPausedTimeEntity | null>(null);

  const { role } = useAuthenticatedAuthUser();

  async function getBrandSales() {
    try {
      setLoading(true);
      const { data } = await getBrandSalesData(id, {
        from: setTimeTo(date[0], 'start').toString(),
        to: setTimeTo(date[1], 'end').toString(),
      });
      setData(data.data);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function getBrandDetails(silent = false) {
    try {
      if (!silent) {
        setLoading(true);
      }
      const { data } = await getBrandById(id);
      setDetails(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      if (silent) {
        setLoading(false);
      }
    }
  }

  async function getProvidersStatuses() {
    try {
      setLoading(true);
      const statusesData = await getBrandProviderStatuses(id);

      setStatuses(statusesData.data.data);

      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function getPausedTimes() {
    try {
      setLoading(true);
      const { data } = await getBrandsPausedTimes(id);
      if (data.data.length > 0) {
        setPausedTimeModal(true);
        setPausedTimes(data.data);
      } else {
        setAddPausedTimeModal(true);
      }
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const [storeName, setStoreName] = useState<MenuNamesResponse | null>(null);

  const isPosLocation = storeName?.locationMode === LocationMode.pos;

  async function getStoreName() {
    try {
      const { data } = await getLocationStoreName(0, id);
      setStoreName(data.data);
    } catch (error) {
      reportError(error);
    }
  }

  useEffect(() => {
    getBrandSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    setBrandVDrive(null);
    setDeliveryAssignment(null);
    getBrandDetails();
    getProvidersStatuses();
    getStoreName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brandDetailsState = {
    brandName: details?.name,
    brandType: details?.type,
    brandImage: details?.imageUrl,
  };

  const handleVDriveOnClick = (status, type) => {
    if (!details) return null;
    if (type === 'vGrubs') {
      return navigate(`/brand/v-drive/${id}`, {
        state: brandDetailsState,
      });
    }

    switch (status) {
      case VDriveStatus.active:
        return navigate(`/brand/v-drive/${id}`, {
          state: brandDetailsState,
        });
      case VDriveStatus.inactive:
        return navigate(`/brand/v-drive/${id}`, {
          state: brandDetailsState,
        });
      case VDriveStatus.inProgress:
        return navigate(`/brand/v-drive/${id}`, {
          state: brandDetailsState,
        });
      case VDriveStatus.rejected:
        return ErrorToast(
          'vDrive has been rejected by vGrubs. To activate it, partner needs to activate it from the restaurant app. '
        );

      case VDriveStatus.disabled:
        return ErrorToast(
          'vDrive has been disabled. To activate it, partner needs to activate it from the restaurant app.'
        );

      case null:
        return ErrorToast(
          'vDrive is not connected. Partner needs to activate it from the restaurant app.'
        );

      default:
        break;
    }
  };

  const actions = [
    {
      label: 'Edit Brand',
      onClick: () => setAddBrandModal(true),
      Icon: DropdownLocationIcon,
    },
    {
      label: 'Manage Providers',
      onClick: () =>
        navigate(`/brands/manage-providers/${id}/${details?.locationId}`),
      Icon: DropdownProvidersIcon,
    },
    {
      label: 'Manage vDrive',
      onClick: () => handleVDriveOnClick(details?.vDriveStatus, details?.type),
      Icon: DropdownVDriveIcon,
    },
    {
      label: 'Manage Menu',
      onClick: () =>
        navigate(`/menu/${details?.locationId}/location/${id}/brand`, {
          state: { name: details?.name },
        }),
      Icon: DropdownMenuIcon,
    },
    {
      label: 'Order Pause Schedule',
      onClick: () => getPausedTimes(),
      Icon: ClockSolidIcon,
    },
    {
      label: 'Edit Notes',
      onClick: () => setNoteModal(true),
      Icon: DropdownNotesIcon,
    },
    {
      // The Menulytics routes are admin and support only.
      label: 'Analyze with Menulytics',
      onClick: () => setMenuHealthModal(true),
      // Their logo, not an icon of ours: it is a partnership with them.
      Icon: (_props: { color?: string }) => <MenulyticsMark />,
      disabled: !details,
      show: role === 'admin' || role === 'support',
    },
    {
      label: 'Orders',
      onClick: () =>
        navigate(`/orders?locationIds=${details?.locationId}&brandIds=${id}`),
      Icon: OrderIcon,
    },
    {
      label: 'Deliveries',
      onClick: () =>
        navigate(
          `/deliveries?locationIds=${details?.locationId}&brandIds=${id}`
        ),
      Icon: DeliveriesIcon,
    },
    {
      label: 'Working Hours',
      onClick: () =>
        navigate(
          `/working-hours?locationIds=${details?.locationId}&brandIds=${id}`
        ),
      Icon: WorkingHoursIcon,
    },
    {
      label: 'Closing Days',
      onClick: () =>
        navigate(
          `/closing-days?locationIds=${details?.locationId}&brandIds=${id}`
        ),
      Icon: ClosingDaysPageIcon,
    },

    {
      label: 'Delete Brand',
      Icon: DropdownDeleteIcon,
      onClick: () => setDeleteModal(id),
      show: role === 'admin' || role === 'support',
    },
  ];

  return (
    <>
      {loading && <AdminSpinner />}

      <AdminHeader title="Brand Details">
        <Div $flex $alignCenter $gap="32px" $gapSM="16px">
          <AdminOptionsDropdown actions={actions} />
        </Div>
      </AdminHeader>

      {details && (
        <AnimatedDiv
          $m="40px 0"
          $flex
          $alignCenter
          $between
          $columnSM
          $gap="30px"
        >
          <Div $flex $alignCenter $gap="30px" $gapSM="16px" $widthSM="100%">
            <BrandImageComponent image={details.imageUrl} />
            <Div $flex $column $gap="4px">
              <Div>
                <H3 $m="0" $color={lightTheme.primary}>
                  {details?.name}
                </H3>
                <H5 $mb="0">{storeName?.locationName}</H5>
              </Div>
              <CopyTextWithIcon
                text={`Store ID: ${details.storeId}`}
                copy={details.storeId.toString()}
              />

              <Div $flex $alignCenter $gap="8px">
                <RenderProviderStatus statuses={statuses} />
              </Div>

              {isPosLocation && details?.pushOrdersToPos && (
                <Div $flex $alignCenter $gap="8px">
                  <PushOrdersToPosPill
                    pushOrdersToPos={details?.pushOrdersToPos}
                    isPos={isPosLocation}
                  />
                </Div>
              )}

              <Div $flex $alignCenter $gap="8px">
                <BrandPausedTimePill pausedTime={details?.paused} />
              </Div>
            </Div>
          </Div>

          <BrandNoteModal
            closeModal={() => {
              setNoteModal(false);
            }}
            modalIsOpen={noteModal}
            setLoading={setLoading}
            setData={setDetails}
            data={details}
            pathPrefix="brands"
          />
        </AnimatedDiv>
      )}

      {data && (
        <AnimatedDiv>
          <BrandAnalyticsSection />
        </AnimatedDiv>
      )}

      <BrandAddModal
        setLoading={setLoading}
        modalIsOpen={addBrandModal}
        closeModal={() => {
          setAddBrandModal(false);
        }}
        setData={setDetails}
        data={details}
        type="edit"
        isPos={isPosLocation}
      />

      <DeleteLocationBrandModal
        isOpen={deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        setLoading={setLoading}
        type={'brands'}
      />

      {details && (
        <MenuHealthReportsModal
          isOpen={menuHealthModal}
          closeModal={() => setMenuHealthModal(false)}
          locationId={String(details.locationId)}
          brand={details}
        />
      )}

      <AllProvidersModalComponent
        allProvidersModal={allProvidersModal}
        setAllProvidersModal={setAllProvidersModal}
      />

      <BrandPausedTimesModal
        modalIsOpen={pausedTimeModal}
        closeModal={() => {
          setPausedTimeModal(false);
        }}
        setLoading={setLoading}
        pausedTimes={pausedTimes}
        setPausedTimes={setPausedTimes}
        setAddModalIsOpen={setAddPausedTimeModal}
        brandId={id}
        setEditPausedTimeModal={setEditPausedTimeModal}
        timezone={details?.timeZone || ''}
        setDetails={setDetails}
      />

      <AddBrandPausedTimesModal
        modalIsOpen={addPausedTimeModal}
        closeModal={() => {
          setAddPausedTimeModal(false);
        }}
        setLoading={setLoading}
        setPausedTimes={setPausedTimes}
        setPausedTimeModal={setPausedTimeModal}
        brandId={id}
        timezone={details?.timeZone || ''}
        setDetails={setDetails}
      />

      <AddBrandPausedTimesModal
        modalIsOpen={editPausedTimeModal}
        closeModal={() => {
          setEditPausedTimeModal(null);
        }}
        setLoading={setLoading}
        setPausedTimes={setPausedTimes}
        setPausedTimeModal={setPausedTimeModal}
        edit
        brandId={id}
        timezone={details?.timeZone || ''}
        setDetails={setDetails}
      />
    </>
  );
};
